/** @jsx jsx */
import { jsx } from '@emotion/react';
import React, { useContext, useEffect } from 'react';
import { SlykitContext } from '/slykitContext';
import { useFetchRootResult } from '/hooks/useFetchRootResult';
import { selectDIL } from 'srs-middleware';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import isEmpty from 'lodash-es/isEmpty';
import uniq from 'lodash-es/uniq';
import { WIDGETS } from '/widgets';
import { FullPageLoader } from 'components/loadingIcon/fullPageLoader';

const { createNewExplorer, updateSelectedDefinition, updateResult, updateWidgets } = WorkspaceActionCreator;

/**
 * Generic Workspace.
 *
 * The Workspace should, on mount, create a new if one does not already
 * exist.  It listens for changes to the layoutStyleKey and will dispatch a change
 * to the creating new cards.
 *
 * TODO: Need to rename things from Explorer to Workspace
 *
 * @returns {null|*}
 * @constructor
 */
export const Workspace = ({
	isReady,
	children,
	widgetOrder,
	widgetInFilter,
	widgetOutFilter,
	explorerKey = 'default',
	rootResultFilter = null,
}) => {
	const {
		state: { savedStates },
	} = useContext(SlykitContext);
	const {
		state: {
			explorer: { layout, selectedDefinitionName },
			transient: { refreshTime, widgets },
		},
		dispatch,
	} = useContext(WorkspaceContext);

	// Only render widgets that are used by this application and that are "valid" as determined by the widget
	// based on the current properties.
	// This needs to update the transient widgets, which should be used to create the correct
	// cards.  This is needed to support the project specific widget ordering.
	useEffect(() => {
		if (!selectedDefinitionName) return;

		let order = uniq((widgetOrder || []).concat(Object.keys(savedStates.widgets)));

		const availableWidgets = order
			.filter((key) => {
				// "application" level filter takes precedence and if we don't have the key, we don't add it.
				if (!Object.keys(savedStates.widgets).includes(key)) return false;

				// optionally filter out or filter in a set of widgets
				if (widgetOutFilter) {
					return !widgetOutFilter.includes(key);
				} else if (widgetInFilter) {
					return widgetInFilter.includes(key);
				}

				return true;
			})
			.map((key) => savedStates.widgets[key])
			.filter(
				(widget) =>
					typeof WIDGETS[widget.key].isValid === 'function' && WIDGETS[widget.key].isValid(selectedDefinitionName)
			);

		dispatch(updateWidgets(availableWidgets));
	}, [widgetOrder, widgetOutFilter, widgetInFilter, savedStates, dispatch, selectedDefinitionName]);

	// if we don't already have an explorer configuration loaded, then gather the
	// default one
	useEffect(() => {
		if (!layout && selectedDefinitionName && !isEmpty(widgets)) {
			dispatch(createNewExplorer());
		}
	}, [dispatch, layout, savedStates, selectedDefinitionName, widgets, explorerKey]);

	// if we don't already have a selected definition, load up the first one, if
	// there isn't a first one, we should ERROR
	useEffect(() => {
		if (!isReady || !!selectedDefinitionName) return;
		dispatch(updateSelectedDefinition(selectDIL()[1], savedStates));
	}, [isReady, dispatch, selectedDefinitionName, savedStates]);

	// updated the filtered result if the selected definition or filters change
	const { rootResult } = useFetchRootResult(selectedDefinitionName, refreshTime, rootResultFilter);

	// if the filtered result changes, update it in the context
	useEffect(() => {
		dispatch(updateResult(rootResult, rootResult));
	}, [rootResult, dispatch]);

	if (!isReady || !layout || !rootResult) {
		return <FullPageLoader />;
	}

	return <React.Fragment>{children}</React.Fragment>;
};
